import { ObjectStatusType, type StudioLibrarySource } from "ts-interfaces";
import { getAuth } from "firebase/auth";
import { getActiveDesignFirmId } from "ui";
import { useFeatureFlagStore } from "../featureFlags/useFeatureFlagStore";
import { firebaseApp } from "./firebase";
import { useStudioLibrary } from "../global-state/useStudioLibrary";
import type { StudioLibraryRef } from "../v2/apis/studioLibraryApi";
import { trackEvent } from "./analyticsManager";

export const STUDIO_LIBRARY_FLAG = "studio_library";
// A placement that fires twice within this window (a synthetic re-drop, a
// retry) counts once.
const DEDUPE_MS = 1500;

// objectStatus / userCreated only: catalog docs can carry a `uid` too, and
// one product must never split into a catalog row and a userProduct row.
const sourceOf = (o: any): StudioLibrarySource =>
  o?.objectStatus === ObjectStatusType.USER_CREATED || o?.userCreated === true
    ? "userProduct"
    : "catalog";

const httpOnly = (v: unknown): string | undefined =>
  typeof v === "string" && /^https?:\/\//.test(v) ? v : undefined;

const parse = (json: string | null | undefined): any => {
  if (!json) return null;
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
};

/** Minimal dataTransfer surface — a real DataTransfer or the armed replay. */
export interface DataTransferLike {
  getData: (key: string) => string;
}

/**
 * The product a library drop stands for, or null for drops that aren't a
 * product choice (text, logos, bare image uploads). Reads keys in the same
 * priority DropPhysicalObject.onDrop does.
 */
export const productRefFromDrop = (
  dt: DataTransferLike | null | undefined,
): StudioLibraryRef | null => {
  if (!dt) return null;
  const upload = parse(dt.getData("upload"));
  if (upload) {
    if (upload.flatProduct !== true || !upload.id) return null;
    return {
      productId: String(upload.id),
      source: sourceOf({ objectStatus: upload.metadata?.objectStatus }),
      name: upload.metadata?.name ?? "",
      supplier: upload.supplier,
      thumbnail: httpOnly(upload.imageUrl),
      // The flat payload doesn't say what the product is; leave it to the doc.
      productType: undefined,
    };
  }
  if (dt.getData("canvasObject") || dt.getData("logos")) return null;
  const material = parse(dt.getData("material"));
  if (material) {
    // The payload's productId first: materialData.id can be a stale or empty
    // id, which would split one product across rows.
    const id = material.productId || material.materialData?.id || material.id;
    if (!id) return null;
    return {
      productId: String(id),
      source: sourceOf(material),
      name: material.name ?? material.materialData?.name ?? "",
      supplier: material.supplier,
      thumbnail: httpOnly(material.materialData?.renderedImage),
      productType: "material",
    };
  }
  const paint = parse(dt.getData("paint"));
  if (paint) {
    const p = paint.paintMaterialsData?.[0];
    const id = paint.productId || p?.id;
    if (!id) return null;
    return {
      productId: String(id),
      source: sourceOf(p),
      name: p?.name ?? "",
      supplier: p?.supplier,
      thumbnail: httpOnly(p?.renderedImage),
      productType: "paint",
    };
  }
  const mesh = parse(dt.getData("mesh"));
  if (mesh?.id) return productRefFromRecord(mesh);
  return null;
};

/** The same ref for a full product record (material apply, object swap). */
export const productRefFromRecord = (
  product: any,
): StudioLibraryRef | null => {
  if (!product?.id) return null;
  return {
    productId: String(product.id),
    source: sourceOf(product),
    name: product.name ?? product.materialData?.name ?? "",
    supplier: product.supplier,
    thumbnail:
      httpOnly(product.materialData?.renderedImage) ??
      httpOnly(product.mesh?.rendered_image) ??
      httpOnly(product.mesh?.thumbnail),
    productType: String(product.productType ?? product.product_type ?? ""),
  };
};

let lastKey = "";
let lastAt = 0;

// The open board's workspace (its design_firm_id; null = Personal), set by
// the board loader. Uses are filed there even if the active workspace was
// switched in another tab. undefined = unknown (new or someone else's board).
let boardWorkspace: string | null | undefined = undefined;
export const setBoardWorkspace = (firmId: string | null | undefined): void => {
  boardWorkspace = firmId;
};

/**
 * A product was placed on, or applied to, the board. Fire-and-forget: never
 * awaited, never throws, no-op when the flag is off, nobody is signed in, or
 * the board isn't a team's.
 */
export const recordBoardUse = (
  ref: StudioLibraryRef | null | undefined,
  via: string = "drop",
): void => {
  try {
    if (!ref) return;
    if (useFeatureFlagStore.getState().flags[STUDIO_LIBRARY_FLAG] !== true) return;
    // Firebase Auth rather than LoginState: LoginState imports libs/util, which
    // imports this module, and that cycle deadlocks vitest's importActual mocks.
    const uid = getAuth(firebaseApp).currentUser?.uid;
    if (!uid) return;
    // A team feature: a Personal board's uses (or, for a board not known yet,
    // uses with Personal active) aren't recorded.
    const firmId =
      boardWorkspace === undefined ? getActiveDesignFirmId(uid) : boardWorkspace;
    if (!firmId) return;
    const key = `${ref.source}:${ref.productId}`;
    const now = Date.now();
    if (key === lastKey && now - lastAt < DEDUPE_MS) return;
    lastKey = key;
    lastAt = now;
    useStudioLibrary
      .getState()
      .bump(uid, ref, boardWorkspace)
      .catch((e) => console.warn("Studio library use not recorded:", e));
    trackEvent("Studio Library - Auto Add", {
      productId: ref.productId,
      source: ref.source,
      via,
    });
  } catch (e) {
    console.warn("recordBoardUse failed:", e);
  }
};

/** Test hook: clears the dedupe window. */
export const __resetBoardUseDedupe = (): void => {
  lastKey = "";
  lastAt = 0;
};
